import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as ejs from 'ejs/ejs.min.js';

import { IoService } from '../../../services/io/io.service';
import { LocationService } from '../../../services/location/location.service';
import { AppComponent } from '../../../app.component';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';
import {
  compileReceiptTemplate,
  renderReceipt,
  ReceiptModelInput,
  RECEIPT_DEFAULT_WIDTH,
  RECEIPT_RUNTIME_VERSION,
} from './receipt-template-runtime';

// Full-screen editor for printed receipt templates (ReceiptTemplate documents),
// opened from the Receipt Templates configuration report (dialogKey:
// 'receipt-template-editor'). Server counterpart: /receiptTemplateEditor
// (gated by ReceiptTemplate.manage).
//
// The preview renders in the browser with the same EJS build and runtime the
// registers use (receipt-template-runtime.ts is shared verbatim with
// RedFireISE), against a real purchase — what you see is what prints.
@Component({
  selector: 'app-receipt-template-editor',
  templateUrl: './receipt-template-editor.component.html',
  styleUrls: ['./receipt-template-editor.component.scss']
})
export class ReceiptTemplateEditorComponent implements OnInit, OnDestroy {

  receiptTemplateId: string = null;
  isNew = false;
  template: any = null;
  saving = false;
  runtimeVersion = RECEIPT_RUNTIME_VERSION;

  locations: any[] = [];
  assigning: { [locationId: string]: boolean } = {};

  previewLocationId: string = null;
  previewPurchases: any[] = [];
  previewPurchaseId: string = null;
  previewData: any = null;
  previewLines: Array<{ text: string; tooLong: boolean }> = [];
  previewError: string = null;
  previewLoading = false;
  ruler = '';

  showReference = false;

  readonly referenceData = [
    { path: 'receipt.location', note: 'clientName, clientNameExtended, name, address.streetAddress, cityStateZip, telephoneFormatted, telephone, website, email' },
    { path: 'receipt.purchase', note: 'key, orderNumber (last four digits of the key — the number the kitchen ticket shows), dateString, date, typeName (SALE/RETURN), type, status, isComplete, customerName, userName, notes[]' },
    { path: 'receipt.items[]', note: 'qty, qtyText, name, text (configured item fields), price, extended, priceText, lines[] (formatted), options[] { name, options[] }, discounts[] { name, amount }, notes[]' },
    { path: 'receipt.discounts[]', note: 'purchase-level discounts: name, amount (cents, negative)' },
    { path: 'receipt.totals', note: 'subtotal, discount, tax, tip, total, due, rounding (cents)' },
    { path: 'receipt.itemCount, receipt.showItemCount', note: 'total item count (whole quantities add up, a weighed line counts once, options ignored) and the client setting showItemCount that the default template gates the Items line on' },
    { path: 'receipt.payments[]', note: 'label, type, amount, tendered, change, rounding, accountName, ledgerName, cardBrand, lastFour, checkNum' },
    { path: 'receipt.hasTransactions', note: 'true when the purchase has any transactions' },
    { path: 'receipt.fulfillments[]', note: 'gift card fulfillments: lastFour, amount' },
    { path: 'receipt.footer', note: 'the location\'s configured footer lines [{ text, style }] or null' },
    { path: 'purchase, location, client, user', note: 'the raw documents, for anything not in receipt.*' },
  ];

  readonly referenceHelpers = [
    { sig: 'h.line(left, right)', note: 'left text with right text at the margin; long left text wraps' },
    { sig: 'h.center(text) / h.left(text) / h.right(text)', note: 'align within the paper width' },
    { sig: 'h.repeat(ch)', note: 'a full-width rule, e.g. h.repeat(\'-\')' },
    { sig: 'h.style(text, style)', note: 'apply a footer style name: center, left, right, repeat' },
    { sig: 'h.money(cents) / h.amount(cents)', note: '$12.34 / 12.34' },
    { sig: 'h.dateTime(v) / h.date(v) / h.time(v)', note: 'locale formatted date and time' },
    { sig: 'h.phone(digits)', note: '(260) 555-1234' },
    { sig: 'h.upper(v) / h.lower(v) / h.truncate(v, n)', note: 'text helpers' },
    { sig: 'h.padLeft(v, n) / h.padRight(v, n)', note: 'fixed-width columns' },
    { sig: 'h.wrap(text)', note: 'word-wrap to the paper width; returns lines' },
  ];

  private previewTimer: any = null;
  private compiled: ((locals: any) => string) | null = null;

  public static showModal(receiptTemplateId: string | null = null): MatDialogRef<ReceiptTemplateEditorComponent> {
    return AppComponent.dialog.open(ReceiptTemplateEditorComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'full-screen-dialog',
      hasBackdrop: true,
      disableClose: false,
      data: {
        receiptTemplateId,
      },
    });
  }

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
    private dialogRef: MatDialogRef<ReceiptTemplateEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.receiptTemplateId = data ? data.receiptTemplateId : null;
  }

  ngOnInit(): void {
    this.isNew = !this.receiptTemplateId;
    // Defer past the dialog's first change detection: showLoading() flips a
    // binding in the app shell synchronously (NG0100 in dev mode otherwise).
    Promise.resolve().then(() => this.load());
  }

  ngOnDestroy(): void {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
  }

  load = async () => {
    LoadingComponent.showLoading();
    try {
      if (this.isNew) {
        const builtIn: any = await this.ioService.post('/receiptTemplateEditor/getDefaultTemplate');
        this.template = this.normalize({ name: '', ejs: builtIn.ejs, width: builtIn.width });
      } else {
        const template = await this.ioService.post('/receiptTemplateEditor/getReceiptTemplate', {
          receiptTemplateId: this.receiptTemplateId,
        });
        this.template = this.normalize(template);
      }
      this.ruler = this.buildRuler(this.template.width);

      this.locations = await this.ioService.post('/receiptTemplateEditor/getLocations') as any[];

      const active = this.locationService.getActiveLocation();
      const activeId = active && active._id ? String(active._id) : null;
      const activeIsRetail = activeId && this.locations.some(l => String(l._id) === activeId);
      this.previewLocationId = activeIsRetail ? activeId : (this.locations.length > 0 ? String(this.locations[0]._id) : null);

      await this.loadPreviewPurchases();
      await this.loadPreviewData();
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to load receipt template.', 'OK', 5000);
      this.dialogRef.close(null);
    }
    LoadingComponent.hideLoading();
  }

  private normalize(doc: any): any {
    const template = { ...doc };
    template.name = template.name || '';
    template.key = template.key || '';
    template.description = template.description || '';
    template.ejs = typeof template.ejs === 'string' ? template.ejs : '';
    template.width = template.width || RECEIPT_DEFAULT_WIDTH;
    template.active = template.active !== false;
    template.isDefault = template.isDefault === true;
    return template;
  }

  private buildRuler(width: number): string {
    let ruler = '';
    for (let i = 1; i <= width; i++) {
      ruler += i % 10 === 0 ? String((i / 10) % 10) : (i % 5 === 0 ? '+' : '·');
    }
    return ruler;
  }

  // ---- preview -------------------------------------------------------------

  async onPreviewLocationChanged() {
    this.previewPurchaseId = null;
    await this.loadPreviewPurchases();
    await this.loadPreviewData();
  }

  async loadPreviewPurchases() {
    this.previewPurchases = [];
    if (!this.previewLocationId) {
      return;
    }
    try {
      this.previewPurchases = await this.ioService.post('/receiptTemplateEditor/getPreviewPurchases', {
        locationId: this.previewLocationId,
      }) as any[];
    } catch (err) {
      this.previewPurchases = [];
    }
  }

  async loadPreviewData() {
    this.previewData = null;
    if (!this.previewLocationId) {
      this.previewError = 'No retail location to preview with.';
      this.previewLines = [];
      return;
    }
    this.previewLoading = true;
    try {
      this.previewData = await this.ioService.post('/receiptTemplateEditor/getPreviewData', {
        locationId: this.previewLocationId,
        purchaseId: this.previewPurchaseId || undefined,
      });
      if (this.previewData && this.previewData.purchase && !this.previewPurchaseId) {
        this.previewPurchaseId = String(this.previewData.purchase._id);
      }
    } catch (err) {
      this.previewError = err.error && err.error.msg ? err.error.msg : 'Unable to load preview data.';
    }
    this.previewLoading = false;
    this.renderPreview();
  }

  templateChanged() {
    this.compiled = null;
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
    this.previewTimer = setTimeout(() => {
      this.previewTimer = null;
      this.renderPreview();
    }, 300);
  }

  renderPreview() {
    this.previewError = null;
    this.previewLines = [];

    if (!this.template) {
      return;
    }
    if (!this.previewData || !this.previewData.purchase) {
      this.previewError = 'No completed purchase at this location to preview with.';
      return;
    }

    try {
      if (!this.compiled) {
        this.compiled = compileReceiptTemplate(ejs, this.template.ejs);
      }
      const width = this.template.width || RECEIPT_DEFAULT_WIDTH;
      const input: ReceiptModelInput = {
        purchase: this.previewData.purchase,
        location: this.previewData.location,
        settings: this.previewData.settings || {},
        client: this.previewData.client,
        itemFields: this.previewData.itemFields || [],
        getAccount: (item: any) => item.account,
        width,
      };
      const text = renderReceipt(this.compiled, input);
      this.previewLines = text.split('\n').map(line => ({ text: line, tooLong: line.length > width }));
    } catch (err) {
      this.compiled = null;
      this.previewError = this.describeError(err);
    }
  }

  private describeError(err: any): string {
    const message = err && err.message ? String(err.message) : String(err);
    // ejs prefixes runtime errors with the failing source excerpt; keep it short.
    const lines = message.split('\n').filter(l => l.trim().length > 0);
    return lines.length > 12 ? lines.slice(0, 12).join('\n') + '\n…' : lines.join('\n');
  }

  get previewPurchaseLabel(): string {
    if (!this.previewData || !this.previewData.purchase) {
      return '';
    }
    const purchase = this.previewData.purchase;
    return 'Chk ' + purchase.key + (purchase.customerName ? ' · ' + purchase.customerName : '');
  }

  get activeTemplateNote(): string {
    if (!this.previewData) {
      return '';
    }
    const active = this.previewData.activeTemplate;
    if (!active) {
      return 'Registers at this location currently print the built-in receipt.';
    }
    return 'Registers at this location currently print "' + active.name + '" (' + active.key + ').';
  }

  // ---- editor --------------------------------------------------------------

  // Tab inserts two spaces instead of leaving the textarea.
  onEditorKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      return;
    }
    event.preventDefault();
    const area = event.target as HTMLTextAreaElement;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const value = area.value;
    area.value = value.substring(0, start) + '  ' + value.substring(end);
    area.selectionStart = area.selectionEnd = start + 2;
    this.template.ejs = area.value;
    this.templateChanged();
  }

  resetToBuiltIn() {
    const confirmRef = AppComponent.showMessage(
      'Reset Template',
      'Replace the template text with the built-in purchase receipt? Unsaved edits will be lost.',
      ['Reset', 'Cancel'],
      false
    );
    confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Reset') {
        return;
      }
      try {
        const builtIn: any = await this.ioService.post('/receiptTemplateEditor/getDefaultTemplate');
        this.template.ejs = builtIn.ejs;
        this.templateChanged();
      } catch (err) {
        AppComponent.showSnack('Unable to load the built-in template.', 'OK', 5000);
      }
    });
  }

  // ---- locations -----------------------------------------------------------

  usesThisTemplate(location: any): boolean {
    return !!this.template.key && location.printTemplateKey === this.template.key;
  }

  async toggleLocation(location: any, use: boolean) {
    if (this.isNew || !this.template.key) {
      return;
    }
    const locationId = String(location._id);
    this.assigning[locationId] = true;
    try {
      const result: any = await this.ioService.post('/receiptTemplateEditor/setLocationTemplate', {
        locationId,
        key: use ? this.template.key : null,
      });
      location.printTemplateKey = result.printTemplateKey;
      AppComponent.showSnack(use
        ? location.name + ' now prints "' + this.template.name + '".'
        : location.name + ' now prints the default template.', 'OK', 3000);
      if (locationId === this.previewLocationId) {
        await this.loadPreviewData();
      }
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to update the location.', 'OK', 5000);
    }
    this.assigning[locationId] = false;
  }

  // ---- save / deactivate ---------------------------------------------------

  private textOrNull(value: any): string | null {
    const text = value === undefined || value === null ? '' : String(value).trim();
    return text.length > 0 ? text : null;
  }

  buildPayload() {
    const payload: any = {
      name: this.textOrNull(this.template.name),
      description: this.textOrNull(this.template.description),
      ejs: this.template.ejs,
      active: this.template.active !== false,
      isDefault: this.template.isDefault === true,
    };
    const key = this.textOrNull(this.template.key);
    if (key) {
      payload.key = key;
    }
    if (!payload.name) {
      delete payload.name;
    }
    return payload;
  }

  async save() {
    if (!this.textOrNull(this.template.name)) {
      AppComponent.showSnack('Receipt template name is required.', 'OK', 5000);
      return;
    }

    // Never save a template that can't compile — the register would fall back silently.
    try {
      compileReceiptTemplate(ejs, this.template.ejs);
    } catch (err) {
      AppComponent.showSnack('Fix the template error before saving: ' + this.describeError(err).split('\n')[0], 'OK', 8000);
      return;
    }

    if (this.saving) {
      return;
    }
    this.saving = true;
    LoadingComponent.showLoading();

    try {
      if (this.isNew) {
        await this.ioService.post('/receiptTemplateEditor/createReceiptTemplate', {
          receiptTemplate: this.buildPayload(),
        });
        AppComponent.showSnack('Receipt template created.', 'OK', 3000);
      } else {
        await this.ioService.post('/receiptTemplateEditor/updateReceiptTemplate', {
          receiptTemplateId: this.receiptTemplateId,
          changes: this.buildPayload(),
        });
        AppComponent.showSnack('Receipt template saved. Registers pick it up on their next sign-in or location load.', 'OK', 5000);
      }
      this.dialogRef.close({ saved: true });
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to save receipt template.', 'OK', 5000);
    }

    this.saving = false;
    LoadingComponent.hideLoading();
  }

  deactivate() {
    const confirmRef = AppComponent.showMessage(
      'Deactivate Receipt Template',
      `Deactivate "${this.template.name}"? Locations using it will print the default template instead.`,
      ['Confirm', 'Cancel'],
      false
    );

    confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') {
        return;
      }
      try {
        await this.ioService.post('/receiptTemplateEditor/deleteReceiptTemplate', {
          receiptTemplateId: this.receiptTemplateId,
        });
        AppComponent.showSnack('Receipt template deactivated.', 'OK', 3000);
        this.dialogRef.close({ saved: true });
      } catch (err) {
        AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to deactivate receipt template.', 'OK', 5000);
      }
    });
  }

  close() {
    this.dialogRef.close(null);
  }
}
